import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@/lib/utils';

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
    React.ComponentRef<typeof TabsPrimitive.List>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & { variant?: 'default' | 'pill' | 'platform' | 'plain' | 'segmented' }
>(({ className, variant = 'default', ...props }, ref) => (
    <TabsPrimitive.List
        ref={ref}
        className={cn(
            'inline-flex h-9 items-center justify-center text-muted-foreground',
            variant === 'default'
                ? 'rounded-lg bg-muted p-1'
                : variant === 'pill'
                  ? 'rounded-full bg-muted/50 p-1'
                : variant === 'platform'
                  ? 'rounded-lg bg-white p-1 text-black'
                : variant === 'segmented'
                  ? 'rounded-xl bg-muted p-1'
                  : 'flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0 text-muted-foreground',
            className
        )}
        {...props}
    />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
    React.ComponentRef<typeof TabsPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & { variant?: 'default' | 'platform' | 'outline' | 'segmented'; size?: 'default' | 'roomy' | 'large' }
>(({ className, variant = 'default', size = 'default', ...props }, ref) => (
    <TabsPrimitive.Trigger
        ref={ref}
        className={cn(
            'inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-full px-3 py-1 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground',
            variant === 'platform' && 'text-black data-[state=active]:bg-zinc-200 data-[state=active]:text-black',
            variant === 'outline' && 'border',
            variant === 'segmented' && 'rounded-lg data-[state=active]:shadow-sm',
            size === 'roomy' && 'px-3.5',
            size === 'large' && 'px-3 py-2',
            className
        )}
        {...props}
    />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
    React.ComponentRef<typeof TabsPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & { spacing?: 'default' | 'none' }
>(({ className, spacing = 'default', ...props }, ref) => (
    <TabsPrimitive.Content
        ref={ref}
        className={cn(
            'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            spacing === 'default' ? 'mt-2' : 'mt-0',
            className
        )}
        {...props}
    />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
